import { getRrjUserDetail } from "@/api/setting";
import { $rrj } from "@/utils/rrjutils";

let currentPermissionCache = null;
let currentPermissionPromise = null;

export function getDefaultScrmDataPermissions() {
  return {
    canViewCorp: true,
    canViewShopName: true,
    canViewProductName: true,
    canSwitchCorp: true,
  };
}

function toBoolean(value, fallback) {
  if (typeof value === "boolean") return value;
  if (value === 1 || value === "1") return true;
  if (value === 0 || value === "0") return false;
  if (value === "true") return true;
  if (value === "false") return false;
  return fallback;
}

export function parseRoleConfigs(configs) {
  if (!configs) return {};
  if (typeof configs === "string") {
    try {
      return JSON.parse(configs);
    } catch (error) {
      return {};
    }
  }
  return typeof configs === "object" ? configs : {};
}

export function normalizeScrmDataPermissions(configs) {
  const defaults = getDefaultScrmDataPermissions();
  const parsedConfigs = parseRoleConfigs(configs);
  const scrmDataPermissions =
    parsedConfigs.scrmDataPermissions || parsedConfigs;

  return {
    canViewCorp: toBoolean(
      scrmDataPermissions.viewOwnedCorp ??
        scrmDataPermissions.viewCorp ??
        scrmDataPermissions.canViewCorp,
      defaults.canViewCorp
    ),
    canViewShopName: toBoolean(
      scrmDataPermissions.viewShopName ??
        scrmDataPermissions.canViewShopName,
      defaults.canViewShopName
    ),
    canViewProductName: toBoolean(
      scrmDataPermissions.viewProductName ??
        scrmDataPermissions.canViewProductName,
      defaults.canViewProductName
    ),
    canSwitchCorp: toBoolean(
      scrmDataPermissions.switchCorp ??
        scrmDataPermissions.canSwitchCorp,
      defaults.canSwitchCorp
    ),
  };
}

export function buildRoleConfigsFromPermissions(permissions, configs) {
  const nextConfigs = parseRoleConfigs(configs);
  nextConfigs.scrmDataPermissions = {
    viewOwnedCorp: !!permissions.canViewCorp,
    viewShopName: !!permissions.canViewShopName,
    viewProductName: !!permissions.canViewProductName,
    switchCorp: !!permissions.canSwitchCorp,
  };
  return nextConfigs;
}

export function clearScrmDataPermissionCache() {
  currentPermissionCache = null;
  currentPermissionPromise = null;
}

export async function getCurrentScrmDataPermissions(forceRefresh = false) {
  const defaults = getDefaultScrmDataPermissions();
  const childId = $rrj.storage.get("_child_id");

  if (!childId) {
    return defaults;
  }

  if (!forceRefresh && currentPermissionCache) {
    return currentPermissionCache;
  }

  if (!forceRefresh && currentPermissionPromise) {
    return currentPermissionPromise;
  }

  currentPermissionPromise = (async () => {
    try {
      const userRes = await getRrjUserDetail(childId);
      const roles = userRes?.data?.roles || [];

      if (!roles.length) {
        currentPermissionCache = defaults;
        return defaults;
      }

      const rolePermissions = roles.map((role) =>
        normalizeScrmDataPermissions(role.configs)
      );

      if (!rolePermissions.length) {
        currentPermissionCache = defaults;
        return defaults;
      }

      const mergedPermissions = rolePermissions.reduce(
        (result, item) => {
          return {
            canViewCorp: result.canViewCorp || item.canViewCorp,
            canViewShopName: result.canViewShopName || item.canViewShopName,
            canViewProductName:
              result.canViewProductName || item.canViewProductName,
            canSwitchCorp: result.canSwitchCorp || item.canSwitchCorp,
          };
        },
        {
          canViewCorp: false,
          canViewShopName: false,
          canViewProductName: false,
          canSwitchCorp: false,
        }
      );

      currentPermissionCache = mergedPermissions;
      return mergedPermissions;
    } catch (error) {
      console.error("获取当前用户 SCRM 数据权限失败:", error);
      return defaults;
    } finally {
      currentPermissionPromise = null;
    }
  })();

  return currentPermissionPromise;
}
